/**
 * Vulnerability Status Label Mapping Utility
 *
 * Maps backend RiskStatus enum values to display labels based on the current
 * display mode (legacy or new terminology).
 *
 * Modes:
 * - legacy: Uses traditional labels (Open, Pending, Closed, Accepted, Rejected)
 * - new: Uses updated labels (Demonstrated, Detected, Resolved, Accepted, Rejected)
 *
 * Uses lookup table pattern to avoid switch/if-else complexity.
 * Cyclomatic complexity: 1 (constant regardless of status count)
 */

import { RiskStatus } from '@/types';

export type VulnerabilityStatusMode = 'legacy' | 'new';

/**
 * Legacy mode status labels (current behavior)
 * Maintains backward compatibility with existing UI
 */
const LEGACY_STATUS_LABELS: Record<RiskStatus, string> = {
  [RiskStatus.Triaged]: 'Pending',
  [RiskStatus.Opened]: 'Open',
  [RiskStatus.Remediated]: 'Closed',
  [RiskStatus.Accepted]: 'Accepted',
  [RiskStatus.Deleted]: 'Rejected',
} as const;

/**
 * New mode status labels (updated terminology)
 * Uses Demonstrated/Detected instead of Open/Pending
 */
const NEW_STATUS_LABELS: Record<RiskStatus, string> = {
  [RiskStatus.Triaged]: 'Detected',
  [RiskStatus.Opened]: 'Demonstrated',
  [RiskStatus.Remediated]: 'Resolved',
  [RiskStatus.Accepted]: 'Accepted',
  [RiskStatus.Deleted]: 'Rejected',
} as const;

/**
 * Returns the display label for a vulnerability status based on the current mode.
 *
 * Uses lookup table pattern to avoid switch/if-else complexity.
 * Cyclomatic complexity: 1 (constant regardless of status count)
 *
 * @param backendStatus - The backend status enum value
 * @param mode - The current vulnerability status mode (legacy or new)
 * @returns The display label for the status
 *
 * @example
 * ```typescript
 * getStatusLabel(RiskStatus.Opened, 'legacy') // 'Open'
 * getStatusLabel(RiskStatus.Opened, 'new')    // 'Demonstrated'
 * ```
 */
export function getStatusLabel(
  backendStatus: RiskStatus,
  mode: VulnerabilityStatusMode
): string {
  const labelMap = mode === 'legacy' ? LEGACY_STATUS_LABELS : NEW_STATUS_LABELS;
  return labelMap[backendStatus] ?? backendStatus;
}

/**
 * Type guard to validate mode values from external sources
 *
 * @param value - The value to check
 * @returns True if value is a valid VulnerabilityStatusMode
 */
export function isValidMode(value: unknown): value is VulnerabilityStatusMode {
  return value === 'legacy' || value === 'new';
}
